/* eslint-disable react/react-in-jsx-scope */
import { Dot, Loader2, Sparkles } from "lucide-react";
import { useMemo } from "react";
import FieldInput from "../FieldInput";

type PaidFile = { id: number; name?: string; uri?: string };

type RealFundingExpectedPaid = {
  id?: number;
  realfunding: number;
  amount?: number; // display already (EUR or converted)
  at: string | Date | null;
  status?: number;
  datePaid?: string | Date | null;
  paymentSlip?: any;
  pjs?: PaidFile[];
};

type RealFundingPaid = {
  id?: number;
  realfunding: number;
  amount?: number; // display already (EUR or converted)
  at: string | Date | null;
  pjs?: PaidFile[];
};

type RealFunding = {
  id: number;
  paidAmount?: number; // display already (EUR or converted)
  dateAmount?: string | Date | null;
  financier?: { id: number; fullname?: string } | number;
  financer?: { id: number; fullname?: string } | number;
  representant?: any;
  pjs?: PaidFile[];
  realFundingExpectedPaids?: RealFundingExpectedPaid[];
  realFundingPaids?: RealFundingPaid[];

  // quand currencySelected != EUR, tu injectes ces champs dans NewFundingByProject
  realFundingExpectedPaidsConverted?: RealFundingExpectedPaid[];
  realFundingPaidsConverted?: RealFundingPaid[];

  verdeo?: boolean;
};

type ProjectIndicator = {
  id: number;
  project?: { id: number };
  year: number;
  month: number; // 0..11 ou 1..12
  type: number; // 1/2/3
  value?: number;
  valueDisplay?: number; // display already (EUR or converted)
};

function toNumber(v: any): number {
  const n = Number(v ?? 0);
  return !n || Number.isNaN(n) ? 0 : n;
}

function toDate(d: any): Date | null {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function normalizeMonth(m: any): number {
  const n = Number(m);
  if (Number.isNaN(n)) return -1;
  if (n >= 0 && n <= 11) return n;
  if (n >= 1 && n <= 12) return n - 1;
  return -1;
}

function pickExpectedPaids(f: any): any[] {
  if (Array.isArray(f?.realFundingExpectedPaidsConverted))
    return f.realFundingExpectedPaidsConverted;
  if (Array.isArray(f?.realFundingExpectedPaids))
    return f.realFundingExpectedPaids;
  return [];
}

function pickPaids(f: any): any[] {
  if (Array.isArray(f?.realFundingPaidsConverted))
    return f.realFundingPaidsConverted;
  if (Array.isArray(f?.realFundingPaids)) return f.realFundingPaids;
  return [];
}

export default function FundingMonthlyMatrix({
  year,
  convertedFundings,
  projectIndicatorsConverted,
  fundingsLoading = false,
  indicatorsLoading = false,
  project,
  currencySelected = "EUR",
  desiredByMonth,
  onCreateContractFromDesired,
  desiredCtaDisabled,
  desiredCount,
}: {
  year: number;
  convertedFundings: RealFunding[];
  projectIndicatorsConverted: ProjectIndicator[];
  fundingsLoading?: boolean;
  indicatorsLoading?: boolean;
  project: any;

  // ✅ NEW (mais safe): utile pour affichage, et cohérent avec tes conversions
  currencySelected?: string;

  // ✅ NEW: desired déjà corrigé (devise -> EUR -> display)
  desiredByMonth?: number[];

  onCreateContractFromDesired?: () => void;
  desiredCtaDisabled?: boolean;
  desiredCount?: number;
}) {
  const isLocal =
    currencySelected !== "EUR" &&
    project?.country?.currency?.code &&
    project.country.currency.code !== "EUR";

  const currencySymbol = useMemo(() => {
    if (!isLocal) return "€";
    return (
      project?.country?.currency?.symbol ||
      project?.country?.currency?.code ||
      ""
    );
  }, [isLocal, project?.country?.currency]);

  const desiredArr = useMemo(() => {
    // fallback (si jamais pas passé) : on prend 0 pour ne pas afficher faux
    const arr = Array(12).fill(0) as number[];
    if (Array.isArray(desiredByMonth) && desiredByMonth.length === 12) {
      for (let i = 0; i < 12; i++) arr[i] = toNumber(desiredByMonth[i]);
      return arr;
    }
    return arr;
  }, [desiredByMonth]);

  const contractArr = useMemo(() => {
    const rec = Array(12).fill(0) as number[];
    for (const f of convertedFundings || []) {
      const amount = toNumber(f?.paidAmount);
      if (!amount) continue;

      const d = toDate(f?.dateAmount);
      const y = d ? d.getFullYear() : year;
      const m = d ? d.getMonth() : 0;

      if (y === year) rec[m] += amount;
    }
    return rec;
  }, [convertedFundings, year]);

  const expectedArr = useMemo(() => {
    const virPrevu = Array(12).fill(0) as number[];
    for (const f of convertedFundings || []) {
      const expected = pickExpectedPaids(f);
      for (const p of expected) {
        const amount = toNumber(p?.amount);
        const d = toDate(p?.at);
        if (!amount || !d || d.getFullYear() !== year) continue;
        virPrevu[d.getMonth()] += amount;
      }
    }
    return virPrevu;
  }, [convertedFundings, year]);

  const receivedArr = useMemo(() => {
    const virRecu = Array(12).fill(0) as number[];
    for (const f of convertedFundings || []) {
      const paids = pickPaids(f);
      if (paids.length > 0) {
        for (const p of paids) {
          const amount = toNumber(p?.amount);
          const d = toDate(p?.at);
          if (!amount || !d || d.getFullYear() !== year) continue;
          virRecu[d.getMonth()] += amount;
        }
      } else {
        // fallback: expected "reçu"
        const expected = pickExpectedPaids(f);
        const receivedExpected = expected.filter((x: any) => x?.status == 2);
        for (const p of receivedExpected) {
          const amount = toNumber(p?.amount);
          const d = toDate(p?.datePaid) ?? toDate(p?.at);
          if (!amount || !d || d.getFullYear() !== year) continue;
          virRecu[d.getMonth()] += amount;
        }
      }
    }
    return virRecu;
  }, [convertedFundings, year]);

  const terrain = useMemo(() => {
    const prev = Array(12).fill(0) as number[];
    const real = Array(12).fill(0) as number[];
    const cert = Array(12).fill(0) as number[];

    const pid = Number(project?.id);
    for (const ind of projectIndicatorsConverted || []) {
      if (Number(ind?.project?.id) !== pid) continue;
      if (Number(ind?.year) !== Number(year)) continue;

      const m = normalizeMonth(ind?.month);
      if (m < 0 || m > 11) continue;

      const val =
        typeof ind?.valueDisplay === "number"
          ? toNumber(ind.valueDisplay)
          : toNumber(ind?.value);

      if (ind.type === 1) prev[m] += val;
      else if (ind.type === 2) real[m] += val;
      else if (ind.type === 3) cert[m] += val;
    }

    return { prev, real, cert };
  }, [projectIndicatorsConverted, project?.id, year]);

  const moneyHint = useMemo(() => {
    return isLocal ? currencySymbol : "€";
  }, [isLocal, currencySymbol]);

  return (
    <div className="space-y-6">
      {/* ====== Vue globale ====== */}
      <div className="flex items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          Vue globale
        </div>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      {/* ====== Block 1: souhaités + contrat signé ====== */}
      <div className="rounded-3xl bg-white px-4 py-4 ring-1 ring-slate-200">
        {fundingsLoading && (
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Chargement des contrats et montants projet...
          </div>
        )}
        <div className="flex items-center gap-3 w-full">
          <div className="w-1/6 text-sm font-medium text-[#586071]">
            <div className="flex items-start justify-between gap-1">
              <div>
                <div>Financements souhaités</div>
                <div className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap">
                  Devise affichée : {moneyHint}
                </div>
                {typeof desiredCount === "number" ? (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {desiredCount} besoin(s)
                  </div>
                ) : null}
              </div>

              {onCreateContractFromDesired ? (
                <>
                  <div>
                    <Dot color="#000" />
                  </div>
                  <span
                    className={
                      "text-blue-700 shrink-0 p-0 border-0 underline cursor-pointer"
                    }
                    onClick={onCreateContractFromDesired}
                    // disabled={!!desiredCtaDisabled}
                    title={
                      desiredCtaDisabled
                        ? "Aucun financement souhaité à couvrir"
                        : "Créer un contrat depuis les financements souhaités"
                    }
                  >
                    J’ai trouvé un financement
                  </span>
                </>
              ) : null}
            </div>
          </div>

          <div className="flex w-5/6 flex-wrap gap-2 mt-8">
            {desiredArr.map((v, i) => (
              <FieldInput
                key={`desired-${project?.id}-${year}-${i}`}
                type={4}
                defaultValue={v || 0}
                valueMultiple={v || 0}
                data={{}}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 w-full">
          <div className="w-1/6 text-sm font-medium text-[#586071]">
            Financement obtenu (Contrat signé)
          </div>

          <div className="flex w-5/6 flex-wrap gap-2">
            {contractArr.map((v, i) => (
              <FieldInput
                key={`contract-${project?.id}-${year}-${i}`}
                type={2}
                defaultValue={v || 0}
                valueMultiple={v || 0}
                data={{}}
              />
            ))}
          </div>
        </div>

        <div className="mt-2 text-[11px] text-muted-foreground">
          Devise affichée : {moneyHint}
        </div>
      </div>

      {/* ====== Block 2: versements prévus + reçus ====== */}
      <div className="rounded-3xl bg-[#F3FAF3] px-4 py-4">
        {fundingsLoading && (
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Chargement des versements du contrat...
          </div>
        )}
        <div className="flex items-center gap-3 mb-3 w-full">
          <div className="w-1/6 text-sm font-medium text-[#586071]">
            Versements prévus
          </div>
          <div className="flex w-5/6 flex-wrap gap-2">
            {expectedArr.map((v, i) => (
              <FieldInput
                key={`expected-${project?.id}-${year}-${i}`}
                type={5}
                defaultValue={v || 0}
                valueMultiple={v || 0}
                data={{}}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 w-full">
          <div className="w-1/6 text-sm font-medium text-[#586071]">
            Versements reçus
          </div>
          <div className="flex w-5/6 flex-wrap gap-2">
            {receivedArr.map((v, i) => (
              <FieldInput
                key={`received-${project?.id}-${year}-${i}`}
                type={6}
                defaultValue={v || 0}
                valueMultiple={v || 0}
                data={{}}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ====== Block 3: terrain prévu/réalisé/certifié ====== */}
      <div className="rounded-3xl bg-[#F7FFF5] px-4 py-4">
        {indicatorsLoading && (
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Chargement des indicateurs terrain...
          </div>
        )}
        <div className="flex items-center gap-3 mb-3 w-full">
          <div className="w-1/6 text-sm font-medium text-[#586071]">
            Versements terrain prévus
          </div>
          <div className="flex w-5/6 flex-wrap gap-2">
            {terrain.prev.map((v, i) => (
              <FieldInput
                key={`terrain-prev-${project?.id}-${year}-${i}`}
                type={1}
                defaultValue={v || 0}
                valueMultiple={v || 0}
                data={{}}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 mb-3 w-full">
          <div className="w-1/6 text-sm font-medium text-[#586071]">
            Versements terrain réalisés
          </div>
          <div className="flex w-5/6 flex-wrap gap-2">
            {terrain.real.map((v, i) => (
              <FieldInput
                key={`terrain-real-${project?.id}-${year}-${i}`}
                type={2}
                defaultValue={v || 0}
                valueMultiple={v || 0}
                data={{}}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 w-full">
          <div className="w-1/6 text-sm font-medium text-[#586071]">
            Versements terrain certifiés
          </div>
          <div className="flex w-5/6 flex-wrap gap-2">
            {terrain.cert.map((v, i) => (
              <FieldInput
                key={`terrain-cert-${project?.id}-${year}-${i}`}
                type={3}
                defaultValue={v || 0}
                valueMultiple={v || 0}
                data={{}}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Petit hint si tu veux */}
      {onCreateContractFromDesired ? (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5" />
          Astuce : “J’ai trouvé un financement” permet de transformer les
          souhaités en contrat obtenu.
        </div>
      ) : null}
    </div>
  );
}
